import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Waves, ExternalLink, Copy, RefreshCw, Loader, TrendingUp, TrendingDown, DollarSign, Search, Filter, X, MessageCircle, Youtube, Globe, Pill, Check, ChevronLeft, ChevronRight, Plus, Eye, Activity } from 'lucide-react';
import { useWalletUser } from '../../hooks/useWalletUser';
import { adminSupabase } from '../../services/supabaseService';
import { globalWidgetTextStyles, widgetClassName } from '../../styles/globalWidgetStyles';
import GlobalDropdown from '../../components/GlobalDropdown';

interface WhaleTransaction {
  id: string;
  tx_id: string;
  token_address: string;
  type: 'buy' | 'sell';
  amount: number;
  price_usd: number;
  volume_usd: number;
  wallet_address: string;
  time: string;
  program: string | null;
  // Token info from join
  token_name: string | null;
  token_symbol: string | null;
  token_image_url: string | null;
  token_website: string | null;
  token_twitter: string | null;
  token_telegram: string | null;
  token_youtube: string | null;
}

interface WhaleTrackerProps {
  onClose?: () => void;
  isWidget?: boolean;
}

const WhaleTracker: React.FC<WhaleTrackerProps> = ({ onClose }) => {
  const walletUser = useWalletUser();
  const [transactions, setTransactions] = useState<WhaleTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'buy' | 'sell'>('all');
  const [solPrice, setSolPrice] = useState<number>(0);

  // Custom filter states
  const [customMinVolume, setCustomMinVolume] = useState('');
  const [customMaxVolume, setCustomMaxVolume] = useState('');
  const [customMinTokenAmount, setCustomMinTokenAmount] = useState('');
  const [customMaxTokenAmount, setCustomMaxTokenAmount] = useState('');
  const [customMinSol, setCustomMinSol] = useState('');
  const [customMaxSol, setCustomMaxSol] = useState('');
  
  // Applied filter states (these are used for actual filtering after search button is clicked)
  const [appliedMinVolume, setAppliedMinVolume] = useState('');
  const [appliedMaxVolume, setAppliedMaxVolume] = useState('');
  const [appliedMinTokenAmount, setAppliedMinTokenAmount] = useState('');
  const [appliedMaxTokenAmount, setAppliedMaxTokenAmount] = useState('');
  const [appliedMinSol, setAppliedMinSol] = useState('');
  const [appliedMaxSol, setAppliedMaxSol] = useState('');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 25;

  // Smart Watchlist state
  const [watchedWallets, setWatchedWallets] = useState<Set<string>>(new Set());
  const [isLoadingWatchlist, setIsLoadingWatchlist] = useState(false);

  // Enhanced realtime animation state
  const [newTransactionIds, setNewTransactionIds] = useState<Set<string>>(new Set());
  const [isRealtimeEnabled, setIsRealtimeEnabled] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [realtimeStats, setRealtimeStats] = useState({
    totalReceived: 0,
    totalFiltered: 0,
    lastUpdate: null as Date | null
  });

  // Refs for cleanup and subscription management
  const supabaseSubscriptionRef = useRef<any>(null);
  const componentMountedRef = useRef(true);

  // Manual search function to apply filters
  const applyFilters = useCallback(() => {
    console.log('🔍 Applying filters manually...', {
      minVolume: customMinVolume,
      maxVolume: customMaxVolume,
      minTokens: customMinTokenAmount,
      maxTokens: customMaxTokenAmount,
      minSol: customMinSol,
      maxSol: customMaxSol
    });

    setIsSearching(true);

    // Apply the current custom filter values
    setAppliedMinVolume(customMinVolume);
    setAppliedMaxVolume(customMaxVolume);
    setAppliedMinTokenAmount(customMinTokenAmount);
    setAppliedMaxTokenAmount(customMaxTokenAmount);
    setAppliedMinSol(customMinSol);
    setAppliedMaxSol(customMaxSol);

    // Reset to first page when applying new filters
    setCurrentPage(1);

    // The useEffect will trigger fetchWhaleTransactions automatically
    setTimeout(() => setIsSearching(false), 1000); // Reset loading state after 1 second
  }, [customMinVolume, customMaxVolume, customMinTokenAmount, customMaxTokenAmount, customMinSol, customMaxSol]);

  // Clear all filters function
  const clearAllFilters = useCallback(() => {
    console.log('🧹 Clearing all filters...');
    
    setIsSearching(true);
    
    // Clear custom filter inputs
    setCustomMinVolume('');
    setCustomMaxVolume('');
    setCustomMinTokenAmount('');
    setCustomMaxTokenAmount('');
    setCustomMinSol('');
    setCustomMaxSol('');
    
    // Clear applied filters
    setAppliedMinVolume('');
    setAppliedMaxVolume('');
    setAppliedMinTokenAmount('');
    setAppliedMaxTokenAmount('');
    setAppliedMinSol('');
    setAppliedMaxSol('');

    // Reset to first page
    setCurrentPage(1);

    // The useEffect will trigger fetchWhaleTransactions automatically
    setTimeout(() => setIsSearching(false), 1000); // Reset loading state after 1 second
  }, []);

  // Fetch SOL price from Binance
  const fetchSolPrice = useCallback(async () => {
    try {
      const response = await fetch('https://api.binance.com/api/v3/ticker/price?symbol=SOLUSDT');
      if (!response.ok) throw new Error('Failed to fetch SOL price');
      const data = await response.json();
      setSolPrice(parseFloat(data.price));
    } catch (error) {
      console.error('Error fetching SOL price:', error);
      // Fallback price if Binance API fails
      setSolPrice(100); // Approximate fallback
    }
  }, []);

  // Load Smart Watchlist wallets
  const loadWatchedWallets = useCallback(async () => {
    if (!walletUser.walletAddress || !walletUser.id) return;
    
    try {
      const { data, error } = await adminSupabase
        .from('watchlist_wallets')
        .select('watched_wallet_address')
        .eq('user_id', walletUser.id)
        .eq('wallet_address', walletUser.walletAddress);

      if (error) {
        console.error('Error loading watched wallets:', error);
        return;
      }

      const walletAddresses = new Set(data?.map(item => item.watched_wallet_address) || []);
      setWatchedWallets(walletAddresses);
      console.log(`📋 Loaded ${walletAddresses.size} watched wallets from Smart Watchlist`);
    } catch (error) {
      console.error('Error loading watched wallets:', error);
    }
  }, [walletUser.walletAddress, walletUser.id]);

  // Fetch whale transactions with optimized query for large datasets
  const fetchWhaleTransactions = useCallback(async () => {
    try {
      setError(null);
      
      // Calculate the actual minimum volume needed
      let actualMinVolume = 0;
      
      // If user has set volume filters, use those to determine database query
      if (appliedMinVolume && appliedMinVolume.trim() !== '') {
        const userMinVolume = parseFloat(appliedMinVolume);
        if (!isNaN(userMinVolume) && userMinVolume >= 0) {
          actualMinVolume = userMinVolume;
        }
      }
      
      // If user has set SOL filters, convert to USD and use the most restrictive
      if (appliedMinSol && appliedMinSol.trim() !== '') {
        const minSol = parseFloat(appliedMinSol);
        if (!isNaN(minSol) && minSol >= 0 && solPrice > 0) {
          const minSolInUsd = minSol * solPrice;
          actualMinVolume = Math.max(actualMinVolume, minSolInUsd);
        }
      }
      
      // If no filters are set, use a very low threshold to get maximum data
      if (actualMinVolume === 0) {
        actualMinVolume = 0.01;
      }
      
      console.log(`🐋 Fetching transactions with volume >= $${actualMinVolume.toFixed(2)}`);
      
      // Build the base query
      let query = adminSupabase
        .from('transactions')
        .select(`
          tx_id,
          token_address,
          type,
          amount,
          price_usd,
          volume_usd,
          wallet_address,
          time,
          program
        `)
        .gte('volume_usd', actualMinVolume);

      // Add maximum volume filter if specified
      if (appliedMaxVolume && appliedMaxVolume.trim() !== '') {
        const userMaxVolume = parseFloat(appliedMaxVolume);
        if (!isNaN(userMaxVolume) && userMaxVolume >= 0) {
          query = query.lte('volume_usd', userMaxVolume);
        }
      }
      
      // Add SOL maximum filter if specified
      if (appliedMaxSol && appliedMaxSol.trim() !== '') {
        const maxSol = parseFloat(appliedMaxSol);
        if (!isNaN(maxSol) && maxSol >= 0 && solPrice > 0) {
          const maxSolInUsd = maxSol * solPrice;
          const currentMaxVolume = appliedMaxVolume ? parseFloat(appliedMaxVolume) : Infinity;
          if (maxSolInUsd < currentMaxVolume) {
            query = query.lte('volume_usd', maxSolInUsd);
          }
        }
      }

      // Add token amount filters
      if (appliedMinTokenAmount && appliedMinTokenAmount.trim() !== '') {
        const minTokens = parseFloat(appliedMinTokenAmount);
        if (!isNaN(minTokens) && minTokens >= 0) {
          query = query.gte('amount', minTokens);
        }
      }
      
      if (appliedMaxTokenAmount && appliedMaxTokenAmount.trim() !== '') {
        const maxTokens = parseFloat(appliedMaxTokenAmount);
        if (!isNaN(maxTokens) && maxTokens >= 0) {
          query = query.lte('amount', maxTokens);
        }
      }

      // Execute the query
      const { data: transactionData, error: transactionError } = await query
        .order('time', { ascending: false })
        .limit(10000);

      if (transactionError) {
        throw transactionError;
      }

      if (!transactionData || transactionData.length === 0) {
        setTransactions([]);
        return;
      }

      // Get unique token addresses
      const tokenAddresses = [...new Set(transactionData.map(tx => tx.token_address))].slice(0, 200);

      // Fetch token metadata
      let tokenData = null;
      try {
        const { data: tokenResponse, error: tokenError } = await adminSupabase
          .from('new_tokens')
          .select(`
            mint,
            name,
            symbol,
            image_url,
            website,
            twitter,
            telegram,
            youtube
          `)
          .in('mint', tokenAddresses);

        if (!tokenError) {
          tokenData = tokenResponse;
        }
      } catch (tokenErr) {
        console.warn('⚠️ Token metadata fetch failed, continuing without metadata');
      }

      // Create token map
      const tokenMap = new Map();
      if (tokenData) {
        tokenData.forEach(token => {
          tokenMap.set(token.mint, token);
        });
      }

      // Transform the data
      const transformedData = transactionData.map(tx => {
        const tokenInfo = tokenMap.get(tx.token_address);
        return {
          id: tx.tx_id,
          tx_id: tx.tx_id,
          token_address: tx.token_address,
          type: tx.type,
          amount: tx.amount,
          price_usd: tx.price_usd,
          volume_usd: tx.volume_usd,
          wallet_address: tx.wallet_address,
          time: tx.time,
          program: tx.program,
          token_name: tokenInfo?.name || null,
          token_symbol: tokenInfo?.symbol || null,
          token_image_url: tokenInfo?.image_url || null,
          token_website: tokenInfo?.website || null,
          token_twitter: tokenInfo?.twitter || null,
          token_telegram: tokenInfo?.telegram || null,
          token_youtube: tokenInfo?.youtube || null,
        };
      });

      setTransactions(transformedData);
      console.log(`✅ WhaleTracker loaded ${transformedData.length} transactions successfully`);
        
    } catch (error) {
      console.error('❌ Error fetching whale transactions:', error);
      setError(error instanceof Error ? error.message : 'Failed to load whale transactions');
    }
  }, [appliedMinVolume, appliedMaxVolume, appliedMinTokenAmount, appliedMaxTokenAmount, appliedMinSol, appliedMaxSol, solPrice]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchSolPrice(), fetchWhaleTransactions(), loadWatchedWallets()]);
      setIsLoading(false);
    };

    loadData();
  }, [fetchSolPrice, fetchWhaleTransactions, loadWatchedWallets]);

  // Re-fetch data when applied filter values change
  useEffect(() => {
    if (appliedMinVolume || appliedMaxVolume || appliedMinTokenAmount || appliedMaxTokenAmount || appliedMinSol || appliedMaxSol) {
      console.log('🔄 Applied filter values changed, re-fetching whale transactions...');
      fetchWhaleTransactions();
    }
  }, [appliedMinVolume, appliedMaxVolume, appliedMinTokenAmount, appliedMaxTokenAmount, appliedMinSol, appliedMaxSol, fetchWhaleTransactions]);

  // Re-fetch data when filter type changes (buy/sell/all) - keep this for immediate feedback
  useEffect(() => {
    console.log('🔄 Filter type changed, re-fetching whale transactions...', {
      filterType: filterType
    });
    fetchWhaleTransactions();
  }, [filterType, fetchWhaleTransactions]);

  // Convert USD to SOL - MOVED UP before matchesCurrentFilters
  const convertUsdToSol = useCallback((usdAmount: number) => {
    if (!solPrice || solPrice === 0) return 0;
    return usdAmount / solPrice;
  }, [solPrice]);

  // Enhanced real-time filter matching function
  const matchesCurrentFilters = useCallback((tx: any) => {
    // Type filter
    if (filterType !== 'all' && tx.type !== filterType) return false;
    
    // Volume USD filter
    if (appliedMinVolume && appliedMinVolume.trim() !== '') {
      const minVol = parseFloat(appliedMinVolume);
      if (!isNaN(minVol) && tx.volume_usd < minVol) return false;
    }
    
    if (appliedMaxVolume && appliedMaxVolume.trim() !== '') {
      const maxVol = parseFloat(appliedMaxVolume);
      if (!isNaN(maxVol) && tx.volume_usd > maxVol) return false;
    }

    // Token amount filter
    if (appliedMinTokenAmount && appliedMinTokenAmount.trim() !== '') {
      const minTokens = parseFloat(appliedMinTokenAmount);
      if (!isNaN(minTokens) && tx.amount < minTokens) return false;
    }
    
    if (appliedMaxTokenAmount && appliedMaxTokenAmount.trim() !== '') {
      const maxTokens = parseFloat(appliedMaxTokenAmount);
      if (!isNaN(maxTokens) && tx.amount > maxTokens) return false;
    }

    // SOL amount filter
    const solVolume = convertUsdToSol(tx.volume_usd);
    if (appliedMinSol && appliedMinSol.trim() !== '') {
      const minSol = parseFloat(appliedMinSol);
      if (!isNaN(minSol) && solVolume < minSol) return false;
    }
    
    if (appliedMaxSol && appliedMaxSol.trim() !== '') {
      const maxSol = parseFloat(appliedMaxSol);
      if (!isNaN(maxSol) && solVolume > maxSol) return false;
    }

    return true;
  }, [filterType, appliedMinVolume, appliedMaxVolume, appliedMinTokenAmount, appliedMaxTokenAmount, appliedMinSol, appliedMaxSol, convertUsdToSol]);

  // Enhanced realtime subscription setup - FIXED DEPENDENCIES
  useEffect(() => {
    if (!isRealtimeEnabled || !componentMountedRef.current) return;

    console.log('🔄 Setting up enhanced WhaleTracker realtime subscription...');

    // Clean up any existing subscription
    if (supabaseSubscriptionRef.current) {
      console.log('🧹 Cleaning up previous subscription...');
      supabaseSubscriptionRef.current.unsubscribe();
      supabaseSubscriptionRef.current = null;
    }

    // Create unique channel name to prevent conflicts
    const channelName = `whale_transactions_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    // Calculate minimum volume threshold for database filter - use current values
    let dbMinVolume = 100; // Lower threshold to get more transactions
    
    const currentMinVolume = appliedMinVolume && appliedMinVolume.trim() !== '' ? parseFloat(appliedMinVolume) : 0;
    const currentMinSol = appliedMinSol && appliedMinSol.trim() !== '' ? parseFloat(appliedMinSol) : 0;
    
    if (!isNaN(currentMinVolume) && currentMinVolume > 0) {
      dbMinVolume = Math.max(dbMinVolume, currentMinVolume);
    }
    
    if (!isNaN(currentMinSol) && currentMinSol > 0 && solPrice > 0) {
      const minSolInUsd = currentMinSol * solPrice;
      dbMinVolume = Math.max(dbMinVolume, minSolInUsd);
    }

    console.log(`🐋 Setting up realtime with DB filter: volume_usd >= $${dbMinVolume}`);

    // Subscribe to new transactions with enhanced error handling
    const transactionsSubscription = adminSupabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'transactions',
          filter: `volume_usd.gte.${dbMinVolume}`
        },
        async (payload) => {
          if (!componentMountedRef.current) return;

          console.log('🐋 New whale transaction detected:', payload);
          
          // Update realtime stats
          setRealtimeStats(prev => ({
            ...prev,
            totalReceived: prev.totalReceived + 1,
            lastUpdate: new Date()
          }));

          const newTx = payload.new;
          
          // Check if this transaction matches our current filters using current state
          const currentFilterType = filterType;
          const currentSearchTerm = searchTerm;
          
          // Type filter
          if (currentFilterType !== 'all' && newTx.type !== currentFilterType) {
            console.log(`⏭️ Transaction type ${newTx.type} doesn't match filter ${currentFilterType}, skipping`);
            return;
          }
          
          // Volume USD filter
          if (currentMinVolume > 0 && newTx.volume_usd < currentMinVolume) {
            console.log(`⏭️ Transaction volume $${newTx.volume_usd} below min $${currentMinVolume}, skipping`);
            return;
          }
          
          const currentMaxVolume = appliedMaxVolume && appliedMaxVolume.trim() !== '' ? parseFloat(appliedMaxVolume) : 0;
          if (currentMaxVolume > 0 && newTx.volume_usd > currentMaxVolume) {
            console.log(`⏭️ Transaction volume $${newTx.volume_usd} above max $${currentMaxVolume}, skipping`);
            return;
          }

          // Token amount filter
          const currentMinTokenAmount = appliedMinTokenAmount && appliedMinTokenAmount.trim() !== '' ? parseFloat(appliedMinTokenAmount) : 0;
          if (currentMinTokenAmount > 0 && newTx.amount < currentMinTokenAmount) {
            console.log(`⏭️ Transaction amount ${newTx.amount} below min ${currentMinTokenAmount}, skipping`);
            return;
          }
          
          const currentMaxTokenAmount = appliedMaxTokenAmount && appliedMaxTokenAmount.trim() !== '' ? parseFloat(appliedMaxTokenAmount) : 0;
          if (currentMaxTokenAmount > 0 && newTx.amount > currentMaxTokenAmount) {
            console.log(`⏭️ Transaction amount ${newTx.amount} above max ${currentMaxTokenAmount}, skipping`);
            return;
          }

          // SOL amount filter
          const solVolume = solPrice > 0 ? newTx.volume_usd / solPrice : 0;
          if (currentMinSol > 0 && solVolume < currentMinSol) {
            console.log(`⏭️ Transaction SOL ${solVolume} below min ${currentMinSol}, skipping`);
            return;
          }
          
          const currentMaxSol = appliedMaxSol && appliedMaxSol.trim() !== '' ? parseFloat(appliedMaxSol) : 0;
          if (currentMaxSol > 0 && solVolume > currentMaxSol) {
            console.log(`⏭️ Transaction SOL ${solVolume} above max ${currentMaxSol}, skipping`);
            return;
          }

          console.log(`✅ Transaction matches filters, adding to list`);
          
          // Update filtered stats
          setRealtimeStats(prev => ({
            ...prev,
            totalFiltered: prev.totalFiltered + 1
          }));

          try {
            // Fetch token metadata for the new transaction
            const { data: tokenData, error: tokenError } = await adminSupabase
              .from('new_tokens')
              .select(`
                mint,
                name,
                symbol,
                image_url,
                website,
                twitter,
                telegram,
                youtube
              `)
              .eq('mint', newTx.token_address)
              .maybeSingle(); // Use maybeSingle to handle cases where token doesn't exist

            if (tokenError && tokenError.code !== 'PGRST116') {
              console.warn('Error fetching token metadata:', tokenError);
            }

            // Transform the new transaction with token info
            const transformedTx: WhaleTransaction = {
              id: newTx.tx_id,
              tx_id: newTx.tx_id,
              token_address: newTx.token_address,
              type: newTx.type,
              amount: newTx.amount,
              price_usd: newTx.price_usd,
              volume_usd: newTx.volume_usd,
              wallet_address: newTx.wallet_address,
              time: newTx.time,
              program: newTx.program,
              token_name: tokenData?.name || null,
              token_symbol: tokenData?.symbol || null,
              token_image_url: tokenData?.image_url || null,
              token_website: tokenData?.website || null,
              token_twitter: tokenData?.twitter || null,
              token_telegram: tokenData?.telegram || null,
              token_youtube: tokenData?.youtube || null,
            };

            // Add to transactions list at the top (only if component is still mounted)
            if (componentMountedRef.current) {
              setTransactions(prev => {
                // Prevent duplicates
                const exists = prev.some(tx => tx.tx_id === newTx.tx_id);
                if (exists) return prev;
                
                // Add new transaction at the top and limit total to prevent memory issues
                const updated = [transformedTx, ...prev];
                return updated.slice(0, 10000); // Keep max 10k transactions in memory
              });
              
              // Add to new transactions for pulsing animation
              setNewTransactionIds(prev => new Set([...prev, newTx.tx_id]));
              
              // Remove from new transactions after 5 seconds
              setTimeout(() => {
                if (componentMountedRef.current) {
                  setNewTransactionIds(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(newTx.tx_id);
                    return newSet;
                  });
                }
              }, 5000);

              console.log(`✅ Added new ${newTx.type} transaction: ${newTx.tx_id} ($${newTx.volume_usd.toLocaleString()})`);
            }
            
          } catch (error) {
            console.error('Error processing new transaction:', error);
            
            // Add transaction without token metadata as fallback
            if (componentMountedRef.current) {
              const transformedTx: WhaleTransaction = {
                id: newTx.tx_id,
                tx_id: newTx.tx_id,
                token_address: newTx.token_address,
                type: newTx.type,
                amount: newTx.amount,
                price_usd: newTx.price_usd,
                volume_usd: newTx.volume_usd,
                wallet_address: newTx.wallet_address,
                time: newTx.time,
                program: newTx.program,
                token_name: null,
                token_symbol: null,
                token_image_url: null,
                token_website: null,
                token_twitter: null,
                token_telegram: null,
                token_youtube: null,
              };

              setTransactions(prev => {
                const exists = prev.some(tx => tx.tx_id === newTx.tx_id);
                if (exists) return prev;
                
                const updated = [transformedTx, ...prev];
                return updated.slice(0, 10000);
              });
              
              setNewTransactionIds(prev => new Set([...prev, newTx.tx_id]));
              
              setTimeout(() => {
                if (componentMountedRef.current) {
                  setNewTransactionIds(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(newTx.tx_id);
                    return newSet;
                  });
                }
              }, 5000);
            }
          }
        }
      )
      .subscribe((status, err) => {
        console.log(`🔄 WhaleTracker subscription status:`, status, err);
        if (status === 'SUBSCRIBED') {
          console.log(`✅ Successfully subscribed to ${channelName}`);
        } else if (status === 'CHANNEL_ERROR') {
          console.error(`❌ Channel error for ${channelName}`);
        } else if (status === 'TIMED_OUT') {
          console.warn(`⏰ Subscription timed out for ${channelName}`);
        }
      });

    // Store subscription reference for cleanup
    supabaseSubscriptionRef.current = transactionsSubscription;

    // Cleanup function
    return () => {
      console.log(`🧹 Cleaning up WhaleTracker realtime subscription: ${channelName}`);
      if (supabaseSubscriptionRef.current) {
        supabaseSubscriptionRef.current.unsubscribe();
        supabaseSubscriptionRef.current = null;
      }
    };
  }, [isRealtimeEnabled]); // SIMPLIFIED DEPENDENCIES - only restart when realtime is toggled

  // Component unmount cleanup
  useEffect(() => {
    return () => {
      componentMountedRef.current = false;
      if (supabaseSubscriptionRef.current) {
        console.log('🧹 Component unmounting, cleaning up subscription...');
        supabaseSubscriptionRef.current.unsubscribe();
        supabaseSubscriptionRef.current = null;
      }
    };
  }, []);

  // Reset realtime stats when filters change - SEPARATE EFFECT
  useEffect(() => {
    console.log('🔄 Filters changed, resetting realtime stats...');
    setRealtimeStats({
      totalReceived: 0,
      totalFiltered: 0,
      lastUpdate: null
    });
  }, [filterType, appliedMinVolume, appliedMaxVolume, appliedMinTokenAmount, appliedMaxTokenAmount, appliedMinSol, appliedMaxSol]);

  // Log filter changes for debugging
  useEffect(() => {
    console.log('🔍 Current filter state:', {
      filterType,
      appliedMinVolume,
      appliedMaxVolume,
      appliedMinTokenAmount,
      appliedMaxTokenAmount,
      appliedMinSol,
      appliedMaxSol,
      searchTerm,
      isRealtimeEnabled
    });
  }, [filterType, appliedMinVolume, appliedMaxVolume, appliedMinTokenAmount, appliedMaxTokenAmount, appliedMinSol, appliedMaxSol, searchTerm, isRealtimeEnabled]);

  // Refresh data
  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      // Only refresh transaction data, not SOL price (which doesn't change frequently)
      await fetchWhaleTransactions();
      console.log('✅ WhaleTracker data refreshed successfully');
    } catch (error) {
      console.error('❌ Error refreshing WhaleTracker data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedAddress(text);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  // Format time ago
  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  // Format currency
  const formatCurrency = (value?: number | null) => {
    if (!value || value === 0) return '$0';
    if (value < 0.01) return `$${value.toFixed(6)}`;
    if (value < 1) return `$${value.toFixed(4)}`;
    if (value < 1000) return `$${value.toFixed(2)}`;
    if (value < 1000000) return `$${(value / 1000).toFixed(1)}K`;
    return `$${(value / 1000000).toFixed(1)}M`;
  };

  // Format SOL amount
  const formatSol = (solAmount: number) => {
    if (solAmount < 0.001) return `${solAmount.toFixed(6)} SOL`;
    if (solAmount < 1) return `${solAmount.toFixed(3)} SOL`;
    if (solAmount < 1000) return `${solAmount.toFixed(2)} SOL`;
    return `${(solAmount / 1000).toFixed(1)}K SOL`;
  };

  // Format address
  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Format token amount
  const formatTokenAmount = (amount: number) => {
    if (amount < 1000) return amount.toFixed(2);
    if (amount < 1000000) return `${(amount / 1000).toFixed(1)}K`;
    if (amount < 1000000000) return `${(amount / 1000000).toFixed(1)}M`;
    return `${(amount / 1000000000).toFixed(1)}B`;
  };

  // Add wallet to Smart Watchlist
  const addToWatchlist = async (walletAddress: string) => {
    if (!walletUser.walletAddress || !walletUser.id) {
      console.error('No user wallet connected or missing user ID');
      return;
    }

    setIsLoadingWatchlist(true);
    try {
      // Use INSERT and handle duplicate errors gracefully
      const { error } = await adminSupabase
        .from('watchlist_wallets')
        .insert({
          user_id: walletUser.id,
          wallet_address: walletUser.walletAddress,
          watched_wallet_address: walletAddress,
          watched_wallet_name: `Whale ${walletAddress.slice(0, 8)}...`,
          notes: 'Added from Whale Tracker',
          priority: 3,
          alert_large_trades: true,
          created_at: new Date().toISOString()
        });

      if (error) {
        // Check if it's a duplicate error (wallet already in watchlist)
        if (error.code === '23505' || error.message?.includes('duplicate') || error.message?.includes('unique_user_watched_wallet')) {
          console.log('Wallet already in watchlist');
          // Update local state anyway to ensure UI consistency
          setWatchedWallets(prev => new Set([...prev, walletAddress]));
          setIsLoadingWatchlist(false);
          return;
        }
        
        console.error('Error adding wallet to watchlist:', error);
        return;
      }

      // Update local state
      setWatchedWallets(prev => new Set([...prev, walletAddress]));
      console.log(`✅ Added wallet ${walletAddress} to Smart Watchlist`);
      
      // Reload watched wallets to ensure sync
      await loadWatchedWallets();
    } catch (error) {
      console.error('Error adding wallet to watchlist:', error);
    } finally {
      setIsLoadingWatchlist(false);
    }
  };

  // Remove wallet from Smart Watchlist
  const removeFromWatchlist = async (walletAddress: string) => {
    if (!walletUser.walletAddress || !walletUser.id) {
      console.error('No user wallet connected or missing user ID');
      return;
    }

    setIsLoadingWatchlist(true);
    try {
      const { error } = await adminSupabase
        .from('watchlist_wallets')
        .delete()
        .eq('user_id', walletUser.id)
        .eq('wallet_address', walletUser.walletAddress)
        .eq('watched_wallet_address', walletAddress);

      if (error) {
        console.error('Error removing wallet from watchlist:', error);
        return;
      }

      // Update local state
      setWatchedWallets(prev => {
        const newSet = new Set(prev);
        newSet.delete(walletAddress);
        return newSet;
      });
      console.log(`✅ Removed wallet ${walletAddress} from Smart Watchlist`);
      
      // Reload watched wallets to ensure sync
      await loadWatchedWallets();
    } catch (error) {
      console.error('Error removing wallet from watchlist:', error);
    } finally {
      setIsLoadingWatchlist(false);
    }
  };

  // Handle whale card click to update widgets with token
  const handleWhaleCardClick = useCallback((tokenAddress: string, tokenSymbol?: string | null, tokenName?: string | null) => {
    console.log(`🐋 Whale card clicked for token: ${tokenSymbol || tokenAddress}`);
    
    // Create a token object for the selected token (matching the Token interface)
    const tokenData = {
      mint: tokenAddress,
      symbol: tokenSymbol || 'UNKNOWN',
      name: tokenName || 'Unknown Token',
      image_url: null, // Will be fetched by the receiving widgets
      website: null,
      twitter: null,
      telegram: null,
      // Add required fields for Token interface
      id: tokenAddress,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      market_cap: null,
      price_usd: null,
      price_change_24h: null,
      volume_24h: null,
      liquidity: null,
      holder_count: null,
      description: null,
      tags: null,
      is_verified: false,
      is_trending: false,
      trending_score: null,
      risk_score: null,
      last_trade_time: null,
      last_price_update: null,
      circulating_supply: null,
      total_supply: null,
      fdv: null,
      ath: null,
      atl: null,
      ath_date: null,
      atl_date: null,
      roi_1h: null,
      roi_24h: null,
      roi_7d: null,
      roi_30d: null,
      roi_1y: null,
      social_score: null,
      developer_score: null,
      community_score: null,
      liquidity_score: null,
      market_cap_rank: null,
      coingecko_id: null,
      coinmarketcap_id: null,
      discord: null,
      reddit: null,
      github: null,
      whitepaper: null,
      explorer: null,
      source: null,
      platform: null,
      contract_address: tokenAddress,
      decimals: null,
      logo: null,
      is_migrated: undefined,
      first_seen_in_trending: null,
      program: null,
      transaction_time: null,
      transaction_type: null,
      transaction_amount: null,
      transaction_price_usd: null,
      sol_volume: null,
      volume_5m: null,
      volume_15m: null,
      volume_30m: null,
      volume_1h: null,
      volume_6h: null,
      volume_12h: null,
      jupiter: null,
      risk: undefined,
      events: undefined,
      pools: undefined,
      dev_wallet: null,
      dev_buy_tokens: null,
      dev_buy_time: null,
      dev_sell_time: null
    };

    // Dispatch the correct events that other widgets listen for
    // 1. Main token selection event
    const event = new CustomEvent('ekko:token_selected', {
      detail: { 
        token: tokenData,
        force_refresh: true
      }
    });
    window.dispatchEvent(event);
    
    // 2. Canvas update event for widget updates
    const canvasUpdateEvent = new CustomEvent('ekko:canvas_update_widgets', {
      detail: { 
        token: tokenData,
        timestamp: Date.now()
      }
    });
    window.dispatchEvent(canvasUpdateEvent);
    
    // 3. Bundle scanner update event
    const bundleScannerEvent = new CustomEvent('ekko:bundle_scanner_update', {
      detail: { 
        token: tokenData,
        timestamp: Date.now()
      }
    });
    window.dispatchEvent(bundleScannerEvent);
    
    console.log(`✅ Dispatched ekko:token_selected, ekko:canvas_update_widgets, and ekko:bundle_scanner_update events for ${tokenSymbol || tokenAddress}`);
  }, []);

  // Filter transactions with custom filters and pagination
  const filteredTransactions = React.useMemo(() => {
    console.log(`🔍 FILTERING DEBUG:`, {
      totalTransactions: transactions.length,
      customMinVolume: appliedMinVolume,
      customMaxVolume: appliedMaxVolume,
      customMinTokenAmount: appliedMinTokenAmount,
      customMaxTokenAmount: appliedMaxTokenAmount,
      customMinSol: appliedMinSol,
      customMaxSol: appliedMaxSol,
      filterType,
      searchTerm
    });

    // Show sample of transaction volumes for debugging
    if (transactions.length > 0) {
      const sampleVolumes = transactions.slice(0, 5).map(tx => ({
        volume_usd: tx.volume_usd,
        type: tx.type,
        token_symbol: tx.token_symbol
      }));
      console.log(`📊 Sample transaction volumes:`, sampleVolumes);
    }

    let filtered = transactions.filter(tx => {
    // Type filter
    if (filterType !== 'all' && tx.type !== filterType) return false;
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
        const matches = (
        tx.token_name?.toLowerCase().includes(search) ||
        tx.token_symbol?.toLowerCase().includes(search) ||
        tx.token_address.toLowerCase().includes(search) ||
        tx.wallet_address.toLowerCase().includes(search) ||
        tx.tx_id.toLowerCase().includes(search)
      );
        if (!matches) return false;
      }

      // Volume USD filter logic - apply both min and max volume filters client-side
      if (appliedMinVolume && appliedMinVolume.trim() !== '') {
        const minVol = parseFloat(appliedMinVolume);
        if (!isNaN(minVol) && tx.volume_usd < minVol) {
          return false;
        }
      }
      
      if (appliedMaxVolume && appliedMaxVolume.trim() !== '') {
        const maxVol = parseFloat(appliedMaxVolume);
        if (!isNaN(maxVol) && tx.volume_usd > maxVol) {
          return false;
        }
      }

      // Token amount filter - single-sided ranges allowed
      if (appliedMinTokenAmount && appliedMinTokenAmount.trim() !== '') {
        const minTokens = parseFloat(appliedMinTokenAmount);
        if (!isNaN(minTokens) && tx.amount < minTokens) return false;
      }
      if (appliedMaxTokenAmount && appliedMaxTokenAmount.trim() !== '') {
        const maxTokens = parseFloat(appliedMaxTokenAmount);
        if (!isNaN(maxTokens) && tx.amount > maxTokens) return false;
      }

      // SOL amount filter - single-sided ranges allowed
      const solVolume = convertUsdToSol(tx.volume_usd);
      if (appliedMinSol && appliedMinSol.trim() !== '') {
        const minSol = parseFloat(appliedMinSol);
        if (!isNaN(minSol) && solVolume < minSol) return false;
      }
      if (appliedMaxSol && appliedMaxSol.trim() !== '') {
        const maxSol = parseFloat(appliedMaxSol);
        if (!isNaN(maxSol) && solVolume > maxSol) return false;
    }
    
    return true;
  });

    console.log(`🎯 FILTERING RESULT:`, {
      originalCount: transactions.length,
      filteredCount: filtered.length,
      appliedFilters: {
        minVolume: !!(appliedMinVolume && appliedMinVolume.trim() !== ''),
        maxVolume: !!(appliedMaxVolume && appliedMaxVolume.trim() !== ''),
        minTokenAmount: !!(appliedMinTokenAmount && appliedMinTokenAmount.trim() !== ''),
        maxTokenAmount: !!(appliedMaxTokenAmount && appliedMaxTokenAmount.trim() !== ''),
        minSol: !!(appliedMinSol && appliedMinSol.trim() !== ''),
        maxSol: !!(appliedMaxSol && appliedMaxSol.trim() !== ''),
        typeFilter: filterType !== 'all',
        searchFilter: !!searchTerm
      }
    });

    // Apply pagination to ALL results (always 25 per page)
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    const totalPages = Math.ceil(filtered.length / recordsPerPage);
    
    return {
      paginatedData: filtered.slice(startIndex, endIndex),
      totalRecords: filtered.length,
      totalPages: totalPages,
      currentPage: currentPage
    };
  }, [transactions, filterType, searchTerm, appliedMinVolume, appliedMaxVolume, appliedMinTokenAmount, appliedMaxTokenAmount, appliedMinSol, appliedMaxSol, currentPage]);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filterType, searchTerm, appliedMinVolume, appliedMaxVolume, appliedMinTokenAmount, appliedMaxTokenAmount, appliedMinSol, appliedMaxSol]);

  if (isLoading) {
    return (
      <div className={`w-full h-full flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm ${widgetClassName}`} data-widget-type="whale-tracker">
        <style>{globalWidgetTextStyles}</style>
        
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-3">
            <Loader className="w-6 h-6 animate-spin text-[#24c35b]" />
            <span className="text-gray-600">Loading whale transactions...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full h-full flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm ${widgetClassName}`} data-widget-type="whale-tracker">
      <style>{globalWidgetTextStyles}</style>
      <style>{`
        /* Hide number input arrows */
        input[type=number]::-webkit-outer-spin-button,
        input[type=number]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type=number] {
          -moz-appearance: textfield;
        }
        
        /* Enhanced pulsing animations for new transactions */
        @keyframes pulse-green {
          0%, 100% { 
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
            border-color: rgba(34, 197, 94, 0.4);
          }
          50% { 
            box-shadow: 0 0 0 10px rgba(34, 197, 94, 0);
            border-color: rgba(34, 197, 94, 0.8);
          }
        }
        
        @keyframes pulse-red {
          0%, 100% { 
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
            border-color: rgba(239, 68, 68, 0.4);
          }
          50% { 
            box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
            border-color: rgba(239, 68, 68, 0.8);
          }
        }
        
        @keyframes pulse-green-dot {
          0%, 100% { 
            box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.7);
            background-color: rgba(34, 197, 94, 1);
          }
          50% { 
            box-shadow: 0 0 0 8px rgba(34, 197, 94, 0);
            background-color: rgba(34, 197, 94, 0.8);
          }
        }
        
        /* Subtle background glow animations for whale cards */
        @keyframes subtle-green-glow {
          0%, 100% { 
            background-color: rgba(34, 197, 94, 0.02);
            box-shadow: inset 0 0 20px rgba(34, 197, 94, 0.05);
          }
          50% { 
            background-color: rgba(34, 197, 94, 0.05);
            box-shadow: inset 0 0 30px rgba(34, 197, 94, 0.1);
          }
        }
        
        @keyframes subtle-red-glow {
          0%, 100% { 
            background-color: rgba(239, 68, 68, 0.02);
            box-shadow: inset 0 0 20px rgba(239, 68, 68, 0.05);
          }
          50% { 
            background-color: rgba(239, 68, 68, 0.05);
            box-shadow: inset 0 0 30px rgba(239, 68, 68, 0.1);
          }
        }
        
        .new-transaction-buy {
          animation: pulse-green 2s ease-in-out 3;
        }
        
        .new-transaction-sell {
          animation: pulse-red 2s ease-in-out 3;
        }
        
        .realtime-indicator {
          animation: pulse-green-dot 2s ease-in-out infinite;
        }
        
        /* Subtle whale card animations */
        .whale-card-buy {
          animation: subtle-green-glow 3s ease-in-out infinite;
        }
        
        .whale-card-sell {
          animation: subtle-red-glow 3s ease-in-out infinite;
        }
      `}</style>

      {/* Header */}
      <div className="p-3 border-b border-gray-200 bg-white flex items-center justify-between rounded-t-xl">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-[#24c35b] rounded-full"></div>
          <h2 className="text-lg font-bold text-black">Whale Tracker</h2>
          {isRealtimeEnabled && (
            <div className="flex items-center ml-2 space-x-2">
              <div className="realtime-indicator w-2 h-2 rounded-full bg-green-400"></div>
              <span className="text-xs text-green-500">Live</span>
              {realtimeStats.totalReceived > 0 && (
                <div className="text-xs text-gray-500">
                  ({realtimeStats.totalFiltered}/{realtimeStats.totalReceived})
                </div>
              )}
            </div>
          )}
        </div>
        {onClose && (
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-50"
            title="Close"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="p-3 border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search tokens, wallets, or transaction IDs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-black placeholder-gray-500 text-sm focus:outline-none focus:border-[#24c35b] focus:ring-1 focus:ring-[#24c35b] hover:border-gray-300"
          />
        </div>
      </div>

      {/* Enhanced Filter Bar with Real-time Controls */}
      <div className="p-3 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <GlobalDropdown
                options={[
                  { value: 'all', label: 'All Types' },
                  { value: 'buy', label: 'Buys Only' },
                  { value: 'sell', label: 'Sells Only' }
                ]}
                value={filterType}
                onChange={(value) => setFilterType(value as 'all' | 'buy' | 'sell')}
              />
            </div>

            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded">SOL: {formatCurrency(solPrice)}</span>
              <button
                onClick={() => setIsRealtimeEnabled(!isRealtimeEnabled)}
                className={`px-3 py-1.5 rounded-lg text-xs transition-all flex items-center border ${
                  isRealtimeEnabled
                    ? 'bg-green-50 border-green-200 text-green-600'
                    : 'bg-gray-50 border-gray-200 text-gray-600'
                }`}
                title={`${isRealtimeEnabled ? 'Disable' : 'Enable'} realtime updates`}
              >
                <div className={`w-2 h-2 rounded-full mr-1 ${isRealtimeEnabled ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`}></div>
                Live
              </button>
              <button
                onClick={refreshData}
                disabled={isRefreshing || isSearching}
                className={`px-3 py-1.5 rounded-lg border text-xs transition-all flex items-center ${
                  isRefreshing || isSearching
                    ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-[#24c35b]/10 border-[#24c35b]/20 text-[#24c35b] hover:bg-[#24c35b]/20'
                }`}
                title={isRefreshing ? 'Refreshing...' : isSearching ? 'Search in progress...' : 'Refresh transaction data'}
              >
                <RefreshCw size={12} className={`mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {/* Real-time Statistics Display */}
        {isRealtimeEnabled && realtimeStats.totalReceived > 0 && (
          <div className="mb-2 p-2 rounded bg-blue-500/10 border border-blue-500/20">
            <div className="flex items-center justify-between text-xs">
              <div className="text-blue-400">
                Real-time: {realtimeStats.totalFiltered} shown / {realtimeStats.totalReceived} received
              </div>
              {realtimeStats.lastUpdate && (
                <div className="text-gray-400">
                  Last update: {formatTimeAgo(realtimeStats.lastUpdate.toISOString())}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Custom Filters - Stacked Rows */}
        <div className="space-y-2">
          {/* Volume USD Filter */}
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Volume USD Range</label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Min $"
                value={customMinVolume}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    setCustomMinVolume(value);
                  }
                }}
                className="flex-1 bg-white border border-gray-200 rounded px-2 py-1 text-black text-xs focus:outline-none focus:border-[#24c35b]/50"
                inputMode="decimal"
              />
              <span className="text-gray-400 text-xs self-center">to</span>
              <input
                type="text"
                placeholder="Max $"
                value={customMaxVolume}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    setCustomMaxVolume(value);
                  }
                }}
                className="flex-1 bg-white border border-gray-200 rounded px-2 py-1 text-black text-xs focus:outline-none focus:border-[#24c35b]/50"
                inputMode="decimal"
              />
            </div>
          </div>

          {/* Token Amount Filter */}
          <div>
            <label className="text-xs text-gray-400 mb-1 block">Token Amount Range</label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Min tokens"
                value={customMinTokenAmount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    setCustomMinTokenAmount(value);
                  }
                }}
                className="flex-1 bg-white border border-gray-200 rounded px-2 py-1 text-black text-xs focus:outline-none focus:border-[#24c35b]/50"
                inputMode="decimal"
              />
              <span className="text-gray-400 text-xs self-center">to</span>
              <input
                type="text"
                placeholder="Max tokens"
                value={customMaxTokenAmount}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    setCustomMaxTokenAmount(value);
                  }
                }}
                className="flex-1 bg-white border border-gray-200 rounded px-2 py-1 text-black text-xs focus:outline-none focus:border-[#24c35b]/50"
                inputMode="decimal"
              />
            </div>
          </div>

          {/* SOL Amount Filter */}
          <div>
            <label className="text-xs text-gray-400 mb-1 block">SOL Amount Range</label>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Min SOL"
                value={customMinSol}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    setCustomMinSol(value);
                  }
                }}
                className="flex-1 bg-white border border-gray-200 rounded px-2 py-1 text-black text-xs focus:outline-none focus:border-[#24c35b]/50"
                inputMode="decimal"
              />
              <span className="text-gray-400 text-xs self-center">to</span>
              <input
                type="text"
                placeholder="Max SOL"
                value={customMaxSol}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                    setCustomMaxSol(value);
                  }
                }}
                className="flex-1 bg-white border border-gray-200 rounded px-2 py-1 text-black text-xs focus:outline-none focus:border-[#24c35b]/50"
                inputMode="decimal"
              />
            </div>
          </div>
        </div>

        {/* Search and Clear Buttons */}
        <div className="flex gap-2 mt-2">
          <button
            onClick={applyFilters}
            disabled={isSearching}
            className={`flex-1 px-3 py-2 rounded border text-sm transition-all flex items-center justify-center ${
              isSearching 
                ? 'bg-gray-600/20 border-gray-600/40 text-gray-400 cursor-not-allowed'
                : 'bg-[#24c35b]/20 border-[#24c35b]/40 text-[#24c35b] hover:bg-[#24c35b]/30'
            }`}
          >
            {isSearching ? (
              <>
                <Loader size={14} className="mr-1 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search size={14} className="mr-1" />
                Search
              </>
            )}
          </button>
          <button
            onClick={clearAllFilters}
            disabled={isSearching}
            className={`px-3 py-2 rounded border text-sm transition-all ${
              isSearching
                ? 'bg-gray-600/10 border-gray-600/20 text-gray-500 cursor-not-allowed'
                : 'bg-gray-600/20 border-gray-600/40 text-gray-400 hover:bg-gray-600/30'
            }`}
          >
            Clear
          </button>
        </div>

        {/* Filter Summary */}
        {(appliedMinVolume || appliedMaxVolume || appliedMinTokenAmount || appliedMaxTokenAmount || appliedMinSol || appliedMaxSol) && (
          <div className="mt-2 p-2 rounded bg-gray-600/5 border border-gray-600/20">
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-400">
                Active filters: {[appliedMinVolume && 'Min Volume', appliedMaxVolume && 'Max Volume', appliedMinTokenAmount && 'Min Tokens', appliedMaxTokenAmount && 'Max Tokens', appliedMinSol && 'Min SOL', appliedMaxSol && 'Max SOL'].filter(Boolean).join(', ')}
              </div>
              <button
                onClick={clearAllFilters}
                className="text-xs text-red-400 hover:text-red-300 transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Pagination Controls - Moved above results */}
      {filteredTransactions.totalPages > 1 && (
        <div className="mx-2 mb-2 flex items-center justify-between border-b border-gray-200 pb-2">
          <div className="text-xs text-black/60">
            Showing {Math.min((currentPage - 1) * recordsPerPage + 1, filteredTransactions.totalRecords)} - {Math.min(currentPage * recordsPerPage, filteredTransactions.totalRecords)} of {filteredTransactions.totalRecords} transactions
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Previous Button */}
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-2 py-1 rounded bg-white border border-gray-200 text-black text-xs hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Previous
            </button>

            {/* Page Numbers */}
            {Array.from({ length: Math.min(5, filteredTransactions.totalPages) }, (_, i) => {
              let pageNum;
              if (filteredTransactions.totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= filteredTransactions.totalPages - 2) {
                pageNum = filteredTransactions.totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-2 py-1 rounded text-xs transition-all ${
                    currentPage === pageNum
                      ? 'bg-[#24c35b]/20 border border-[#24c35b]/40 text-[#24c35b]'
                      : 'bg-white border border-gray-200 text-black hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}

            {/* Next Button */}
            <button
              onClick={() => setCurrentPage(prev => Math.min(filteredTransactions.totalPages, prev + 1))}
              disabled={currentPage === filteredTransactions.totalPages}
              className="px-2 py-1 rounded bg-white border border-gray-200 text-black text-xs hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Content - Exact SniperBot styling */}
      <div className="flex-grow overflow-y-auto px-2 py-1.5" style={{ background: '#ffffff', borderRadius: 0, overflowX: 'visible' }}>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="text-red-600 text-sm font-medium mb-1">Error Loading Data</div>
            <p className="text-red-500 text-xs">{error}</p>
          </div>
        )}

        {filteredTransactions.paginatedData.length === 0 ? (
          <div className="text-center py-8">
            <Waves className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">
              {searchTerm ? 'No whale transactions found matching your search' : 'No whale transactions found'}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {filteredTransactions.paginatedData.map((tx) => (
              <div 
                key={tx.id} 
                className={`p-2 rounded border border-gray-600/20 bg-gray-600/5 hover:bg-gray-600/10 transition-all cursor-pointer ${
                  newTransactionIds.has(tx.tx_id) 
                    ? tx.type === 'buy' 
                      ? 'new-transaction-buy' 
                      : 'new-transaction-sell'
                    : ''
                } ${
                  tx.type === 'buy' ? 'whale-card-buy' : 'whale-card-sell'
                }`}
                onClick={(e) => {
                  // Only trigger if we're not clicking on interactive elements
                  const target = e.target as HTMLElement;
                  const isInteractive = target.closest('button, a, input, select, textarea');
                  
                  if (!isInteractive) {
                    handleWhaleCardClick(tx.token_address, tx.token_symbol, tx.token_name);
                  }
                }}
                title="Click to select this token in other widgets"
              >
                {/* Compact Transaction Header */}
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center space-x-2">
                    {tx.token_image_url ? (
                      <img
                        src={tx.token_image_url}
                        alt={tx.token_symbol || 'Token'}
                        className="w-6 h-6 rounded-full border border-white/10"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-6 h-6 bg-gray-600 rounded-full flex items-center justify-center border border-white/10">
                        <span className="text-[10px] text-gray-300">
                          {tx.token_symbol?.charAt(0) || '?'}
                        </span>
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center space-x-1">
                        <h3 className="font-medium text-white text-xs">
                          {tx.token_name || 'Unknown Token'}
                        </h3>
                        <span className="text-[10px] text-gray-400">
                          ({tx.token_symbol || 'UNKNOWN'})
                        </span>
                        <div className={`px-1.5 py-0.5 rounded text-[9px] font-medium border ${
                          tx.type === 'buy' 
                            ? 'bg-green-500/20 text-green-400 border-green-500/40' 
                            : 'bg-red-500/20 text-red-400 border-red-500/40'
                        }`}>
                          {tx.type === 'buy' ? (
                            <div className="flex items-center space-x-0.5">
                              <TrendingUp className="w-2 h-2" />
                              <span>BUY</span>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-0.5">
                              <TrendingDown className="w-2 h-2" />
                              <span>SELL</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-1 mt-0.5">
                        <span className="text-[10px] text-gray-500">
                          {formatAddress(tx.token_address)}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(tx.token_address);
                          }}
                          className="text-gray-400 hover:text-white transition-colors"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <a
                          href={`https://solscan.io/token/${tx.token_address}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-400 hover:text-[#24c35b] transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    {/* Transaction Details - Moved to right side */}
                    <div className="grid grid-cols-4 gap-2 mb-1 text-[9px]">
                  <div>
                        <span className="text-gray-400">Vol:</span>
                        <div className={`font-bold text-[9px] ${tx.type === 'buy' ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(tx.volume_usd)}
                      </div>
                      </div>
                      <div>
                        <span className="text-gray-400">SOL:</span>
                        <div className={`font-bold text-[9px] ${tx.type === 'buy' ? 'text-green-400' : 'text-red-400'}`}>
                        {formatSol(convertUsdToSol(tx.volume_usd))}
                      </div>
                      </div>
                      <div>
                        <span className="text-gray-400">Amt:</span>
                        <div className="text-white text-[9px]">
                          {formatTokenAmount(tx.amount)}
                    </div>
                  </div>
                  <div>
                        <span className="text-gray-400">Price:</span>
                        <div className="text-white text-[9px]">
                          {formatCurrency(tx.price_usd)}
                      </div>
                      </div>
                    </div>
                    <div className="text-[10px] text-gray-400">
                      {formatTimeAgo(tx.time)}
                    </div>
                  </div>
                </div>

                {/* Compact Wallet Info */}
                <div className="bg-green-500/20 border border-green-500/40 rounded p-1.5 mb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-1">
                      <span className="text-[10px] font-medium text-green-300">Wallet</span>
                      <span className="text-[9px] text-gray-400 font-mono">
                        {formatAddress(tx.wallet_address)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      {/* Smart Watchlist Button */}
                      {watchedWallets.has(tx.wallet_address) ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFromWatchlist(tx.wallet_address);
                          }}
                          disabled={isLoadingWatchlist}
                          className="px-1.5 py-0.5 rounded text-[9px] bg-blue-500/20 text-blue-400 border border-blue-500/40 hover:bg-blue-500/30 transition-all flex items-center"
                          title="Remove from Smart Watchlist"
                        >
                          <Eye size={8} className="mr-0.5" />
                          Tracked
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            addToWatchlist(tx.wallet_address);
                          }}
                          disabled={isLoadingWatchlist}
                          className="px-1.5 py-0.5 rounded text-[9px] bg-gray-600/20 text-gray-400 border border-gray-600/40 hover:bg-[#24c35b]/20 hover:text-[#24c35b] hover:border-[#24c35b]/40 transition-all flex items-center"
                          title="Add to Smart Watchlist"
                        >
                          <Plus size={8} className="mr-0.5" />
                          Watch
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(tx.wallet_address);
                        }}
                        className="text-gray-400 hover:text-white transition-colors"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <a
                        href={`https://solscan.io/account/${tx.wallet_address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-[#24c35b] transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                  {tx.program && (
                        <span className="text-[9px] text-gray-400 ml-1">{tx.program}</span>
                  )}
                    </div>
                  </div>
                </div>

                {/* Bottom section - Social links on left, TX info on right */}
                <div className="flex items-center justify-between">
                  {/* Social Links */}
                  {(tx.token_website || tx.token_twitter || tx.token_telegram || tx.token_youtube) ? (
                    <div className="flex gap-1">
                      {/* X.com Search */}
                    <a
                        href={`https://x.com/search?q=${tx.token_address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                        className="bg-black rounded-full w-6 h-6 flex items-center justify-center hover:opacity-80"
                        title="Search token address on Twitter"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Search size={16} className="text-white" />
                    </a>

                      {/* Pump.fun pill */}
                        <a
                        href={`https://pump.fun/coin/${tx.token_address}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        className="bg-black rounded-full w-6 h-6 flex items-center justify-center hover:opacity-80"
                        title="View on Pump.fun"
                        onClick={(e) => e.stopPropagation()}
                        >
                        <Pill size={16} className="text-white" />
                        </a>

                      {/* Twitter/X Official */}
                      {tx.token_twitter && (
                        <a
                          href={tx.token_twitter}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-black rounded-full w-6 h-6 flex items-center justify-center hover:opacity-80"
                          title="Official Twitter"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <img 
                            src="https://cdn.cms-twdigitalassets.com/content/dam/about-twitter/x/brand-toolkit/logo-black.png.twimg.2560.png" 
                            alt="X" 
                            className="w-4 h-4 invert"
                          />
                        </a>
                      )}

                      {/* Telegram */}
                      {tx.token_telegram && (
                        <a
                          href={tx.token_telegram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white bg-[#0088cc] rounded-full w-6 h-6 flex items-center justify-center hover:opacity-80"
                          title="Official Telegram"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MessageCircle size={16} />
                        </a>
                      )}

                      {/* YouTube */}
                      {tx.token_youtube && (
                        <a
                          href={tx.token_youtube}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white bg-[#FF0000] rounded-full w-6 h-6 flex items-center justify-center hover:opacity-80"
                          title="Official YouTube"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Youtube size={16} />
                        </a>
                      )}

                      {/* Website */}
                      {tx.token_website && (
                        <a
                          href={tx.token_website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white bg-gray-600 rounded-full w-6 h-6 flex items-center justify-center hover:opacity-80"
                          title="Official Website"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Globe size={16} />
                        </a>
                      )}

                      {/* Solscan Token */}
                      <a
                        href={`https://solscan.io/token/${tx.token_address}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-black rounded-full w-6 h-6 flex items-center justify-center hover:opacity-80"
                        title="View token on Solscan"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ExternalLink size={16} className="text-white" />
                      </a>
                    </div>
                  ) : (
                    <div></div>
                  )}

                  {/* Transaction Info - Bottom Right */}
                  <div className="flex items-center space-x-1">
                    <span className="text-[9px] text-gray-400">TX:</span>
                    <span className="text-[9px] text-gray-300 font-mono">
                      {formatAddress(tx.tx_id)}
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(tx.tx_id);
                      }}
                      className="text-gray-400 hover:text-white transition-colors"
                      title="Copy transaction ID"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <a
                      href={`https://solscan.io/tx/${tx.tx_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-[#24c35b] transition-colors"
                      title="View transaction on Solscan"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WhaleTracker;

// Widget factory function
export const createWhaleTrackerWidget = () => ({
  id: 'whale-tracker',
  name: 'Whale Tracker',
  title: 'Whale Tracker',
  type: 'whale-tracker-widget',
  content: <WhaleTracker />,
  size: { width: 700, height: 600 },
  minSize: { width: 500, height: 400 },
  maxSize: { width: 900, height: 800 },
  category: 'Analytics',
  glassMorphism: true,
  containerStyle: {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(12px)",
    borderRadius: "12px",
    border: "1px solid rgba(36, 195, 91, 0.2)",
    boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(36, 195, 91, 0.2)",
    overflow: "hidden",
  },
}); 